import { Button } from "@/components/ui/button";
import { X, AlertTriangle } from "lucide-react";
import { formatRentangTanggal } from "@/lib/datetime";
import { BLOK_JAGA, BLOK_STASE_LUAR, BLOK_STASE_RSCM, LABEL_BLOK } from "@/lib/rotasiCepat";
import { hariDalamRentang, hariTerbanyakSebulan, kelompokJaga } from "@/lib/kalenderRotasi";

const namaDari = (daftar, id) => daftar.find((x) => x.id === id)?.nama;

function tempatStase(blok, b, master) {
    if (blok === BLOK_STASE_LUAR) return b.nama_rs;
    return [namaDari(master.departemen, b.departemen_id), namaDari(master.divisi, b.divisi_id),
        namaDari(master.instalasi, b.instalasi_id)].filter(Boolean).join(" — ") || "-";
}

function Pesan({ galat, peringatan }) {
    if (!galat?.length && !peringatan?.length) return null;
    return (
        <div className="mt-0.5 space-y-0.5 text-[11px]">
            {(galat || []).map((g, i) => <div key={`g${i}`} className="text-rose-700" role="alert">{g}</div>)}
            {(peringatan || []).map((p, i) => (
                <div key={`p${i}`} className="text-amber-700 inline-flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{p}
                </div>
            ))}
        </div>
    );
}

// Daftar baris baru ("akan disimpan") di bawah kalender. Hanya baris BARU -- data tersimpan tidak bisa
// diubah/dihapus dari sini (tetap lewat Edit Peserta > Rotasi Klinik).
export default function DaftarRotasiBaru({ form, master, galat, peringatan, ambang, onHapus }) {
    const stase = [BLOK_STASE_RSCM, BLOK_STASE_LUAR].flatMap((blok) => form[blok].map((b, i) => ({ blok, b, i })));
    const jaga = kelompokJaga(form);
    if (!stase.length && !jaga.length) {
        return <div className="text-xs text-slate-400" data-testid="rc-daftar-kosong">Belum ada rotasi baru. Pilih tanggal di kalender.</div>;
    }
    return (
        <ul className="space-y-1.5 text-xs" data-testid="rc-daftar">
            {stase.map(({ blok, b, i }) => {
                const hari = hariDalamRentang(b.tanggal_mulai, b.tanggal_akhir).length;
                const penuh = ambang && hariTerbanyakSebulan(b.tanggal_mulai, b.tanggal_akhir) >= ambang;
                return (
                    <li key={b.key} className={`rounded border px-2 py-1 ${galat[b.key] ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-white"}`}
                        data-testid={`rc-baris-${blok}-${i}`}>
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <span className="font-semibold text-slate-800">{LABEL_BLOK[blok]}</span>
                                <span className="text-slate-600"> · {tempatStase(blok, b, master)}</span>
                                <span className="text-slate-500"> · {formatRentangTanggal(b.tanggal_mulai, b.tanggal_akhir)} ({hari} hari)</span>
                                {penuh && <span className="ml-1 text-amber-700">· hampir sebulan penuh</span>}
                            </div>
                            <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-500 shrink-0"
                                onClick={() => onHapus(blok, b.key)} aria-label={`Buang ${LABEL_BLOK[blok]} ${formatRentangTanggal(b.tanggal_mulai, b.tanggal_akhir)} dari daftar`}
                                data-testid={`rc-hapus-${blok}-${i}`}>
                                <X className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                        <Pesan galat={galat[b.key]} peringatan={peringatan[b.key]} />
                    </li>
                );
            })}
            {jaga.map((k) => (
                <li key={k.kunci} className="rounded border border-slate-200 bg-white px-2 py-1" data-testid="rc-kelompok-jaga">
                    <div>
                        <span className="font-semibold text-slate-800">Jaga</span>
                        <span className="text-slate-600"> · {[namaDari(master.departemen, k.departemen_id), namaDari(master.instalasi, k.instalasi_id)].filter(Boolean).join(" — ")}</span>
                        <span className="text-slate-500"> ({k.baris.length} tanggal)</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                        {k.baris.map((b) => (
                            <span key={b.key} className={`inline-flex items-center gap-0.5 rounded-full border pl-2 pr-0.5 ${galat[b.key] ? "border-rose-300 bg-rose-50 text-rose-700" : "border-amber-300 bg-amber-50 text-slate-800"}`}>
                                {formatRentangTanggal(b.tanggal_mulai, b.tanggal_mulai)}
                                <button type="button" className="rounded-full p-0.5 hover:bg-slate-200" onClick={() => onHapus(BLOK_JAGA, b.key)}
                                    aria-label={`Buang Jaga ${formatRentangTanggal(b.tanggal_mulai, b.tanggal_mulai)} dari daftar`}>
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                    {k.baris.filter((b) => galat[b.key]).map((b) => (
                        <div key={b.key} className="mt-0.5 text-[11px] text-rose-700" role="alert">
                            {formatRentangTanggal(b.tanggal_mulai, b.tanggal_mulai)}: {galat[b.key].join(" · ")}
                        </div>
                    ))}
                </li>
            ))}
        </ul>
    );
}
